import { Helmet } from './helmet'
import { useEffect, type JSX } from 'react'
import type { PageSeoMetadata } from './routes'

export interface SeoProps {
  metadata: PageSeoMetadata
  /** Compatibility projections; new consumers should read metadata. */
  title: string
  description: string
  canonical: string
  noindex?: boolean
  /** Runtime-only override for app shells; static builds use the source contract defaultOgImage. */
  ogImage?: string
}

interface LegacySeoProps {
  title: string
  description: string
  canonical: string
  /** Locale for a dynamic runtime-only page. */
  locale?: string
  ogImage?: string
  noindex?: boolean
}

interface NormalizedSeoProps {
  metadata: PageSeoMetadata
  ogImage?: string
}

export function Seo(props: SeoProps): JSX.Element
export function Seo(props: NormalizedSeoProps): JSX.Element
/** Transitional runtime overload for non-SSG dynamic pages. */
export function Seo(props: LegacySeoProps): JSX.Element
export function Seo(props: SeoProps | NormalizedSeoProps | LegacySeoProps) {
  const sourceMetadata = 'metadata' in props ? props.metadata : legacyRuntimeMetadata(props)
  const { ogImage } = props
  const metadata = ogImage ? {
    ...sourceMetadata,
    openGraph: { ...sourceMetadata.openGraph, image: ogImage },
    twitter: { ...sourceMetadata.twitter, image: ogImage, card: 'summary_large_image' as const },
  } : sourceMetadata
  const { title, description, canonical, robots, openGraph, twitter, alternates, schema } = metadata

  return (
    <>
      <RouteMetadataCleanup metadata={metadata} />
      <Helmet htmlAttributes={{ lang: metadata.locale }}>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content={`${robots.index ? 'index' : 'noindex'}, ${robots.follow ? 'follow' : 'nofollow'}`} />
        <link rel="canonical" href={canonical} />
        {alternates.mode === 'localized' && alternates.links.map((link) => (
          <link key={`${link.hreflang}:${link.href}`} rel="alternate" hrefLang={link.hreflang} href={link.href} />
        ))}
        <meta property="og:title" content={openGraph.title} />
        <meta property="og:description" content={openGraph.description} />
        <meta property="og:type" content={openGraph.type} />
        <meta property="og:url" content={openGraph.url} />
        {openGraph.image && <meta property="og:image" content={openGraph.image} />}
        <meta name="twitter:card" content={twitter.card} />
        <meta name="twitter:title" content={twitter.title} />
        <meta name="twitter:description" content={twitter.description} />
        {twitter.image && <meta name="twitter:image" content={twitter.image} />}
        {schema.mode === 'required' && schema.schemas.map((item, index) => (
          <script
            key={index}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(item).replace(/</g, '\\u003c') }}
          />
        ))}
      </Helmet>
    </>
  )
}

interface RouteMetadataCleanupProps {
  metadata: PageSeoMetadata
}

interface RouteMetadataElement {
  content?: string
  href?: string
  textContent?: string | null
  getAttribute: (qualifiedName: string) => string | null
  remove: () => void
}

interface RouteMetadataDocument {
  head: {
    querySelectorAll: (selectors: string) => ArrayLike<RouteMetadataElement>
  }
}

function RouteMetadataCleanup({ metadata }: RouteMetadataCleanupProps) {
  useEffect(() => {
    const document = (globalThis as { document?: RouteMetadataDocument }).document
    if (document) {
      deduplicateRouteMetadata(document, metadata)
    }
  }, [metadata])

  return null
}

export function deduplicateRouteMetadata(document: RouteMetadataDocument, metadata: PageSeoMetadata) {
  const groups: RouteMetadataGroup[] = [
    // Static SSG tags do not have React Helmet's data-rh attribute. Route SEO
    // owns these fields, so remove stale static and runtime tags alike on SPA navigation.
    { selector: 'title', values: new Set([metadata.title]), value: (element) => element.textContent ?? '' },
    { selector: 'meta[name="description"]', values: new Set([metadata.description]), value: contentValue },
    { selector: 'meta[name="robots"]', values: new Set([`${metadata.robots.index ? 'index' : 'noindex'}, ${metadata.robots.follow ? 'follow' : 'nofollow'}`]), value: contentValue },
    { selector: 'link[rel="canonical"]', values: new Set([metadata.canonical]), value: (element) => element.href ?? '' },
    {
      selector: 'link[rel="alternate"]',
      values: new Set(metadata.alternates.mode === 'localized'
        ? metadata.alternates.links.map((link) => `${link.hreflang}:${link.href}`)
        : []),
      value: (element) => `${element.getAttribute('hreflang') ?? ''}:${element.href ?? ''}`,
    },
    {
      selector: 'meta[property="og:title"], meta[property="og:description"], meta[property="og:type"], meta[property="og:url"], meta[property="og:image"]',
      values: new Set([
      `og:title:${metadata.openGraph.title}`,
      `og:description:${metadata.openGraph.description}`,
      `og:type:${metadata.openGraph.type}`,
      `og:url:${metadata.openGraph.url}`,
      ...(metadata.openGraph.image ? [`og:image:${metadata.openGraph.image}`] : []),
      ]),
      value: (element) => `${element.getAttribute('property') ?? ''}:${element.content ?? ''}`,
    },
    {
      selector: 'meta[name^="twitter:"]',
      values: new Set([
      `twitter:card:${metadata.twitter.card}`,
      `twitter:title:${metadata.twitter.title}`,
      `twitter:description:${metadata.twitter.description}`,
      ...(metadata.twitter.image ? [`twitter:image:${metadata.twitter.image}`] : []),
      ]),
      value: (element) => `${element.getAttribute('name') ?? ''}:${element.content ?? ''}`,
    },
    {
      selector: 'script[type="application/ld+json"]',
      values: new Set(metadata.schema.mode === 'required' ? metadata.schema.schemas.map(schemaText) : []),
      value: (element) => element.textContent ?? '',
    },
  ]

  groups.forEach(({ selector, values, value }) => removeStaleMetadata(
    Array.from(document.head.querySelectorAll(selector)),
    values,
    value,
  ))
}

interface RouteMetadataGroup {
  selector: string
  values: Set<string>
  value: (element: RouteMetadataElement) => string
}

function contentValue(element: RouteMetadataElement): string {
  return element.content ?? ''
}

function removeStaleMetadata(
  elements: RouteMetadataElement[],
  expected: Set<string>,
  getValue: (element: RouteMetadataElement) => string,
) {
  const retained = new Set<string>()
  elements.forEach((element) => {
    const value = getValue(element)
    if (expected.has(value) && !retained.has(value)) {
      retained.add(value)
      return
    }
    element.remove()
  })
}

function schemaText(schema: object): string {
  return JSON.stringify(schema).replace(/</g, '\\u003c')
}

/** Materializes the compatibility contract used by runtime-only dynamic pages. */
export function legacyRuntimeMetadata(props: LegacySeoProps): PageSeoMetadata {
  const url = new URL(props.canonical)
  const social = {
    title: props.title,
    description: props.description,
    url: props.canonical,
    ...(props.ogImage ? { image: props.ogImage } : {}),
  }
  return {
    path: url.pathname || '/',
    title: props.title,
    description: props.description,
    canonical: props.canonical,
    locale: props.locale ?? 'ru',
    alternates: { mode: 'none', reason: 'Dynamic runtime-only page.' },
    robots: { index: !props.noindex, follow: true },
    openGraph: { ...social, type: 'website' },
    twitter: { ...social, card: props.ogImage ? 'summary_large_image' : 'summary' },
    schema: { mode: 'none', reason: 'Dynamic runtime-only page.' },
    sitemap: { include: false },
  }
}
